import { ReactRenderer } from "@tiptap/react";
import { RefObject } from "react";
import tippy, { GetReferenceClientRect, Instance, Props } from "tippy.js";

import { SuggestionProps } from ".";
import { filterSuggestionGroups, isQueryAbandoned } from "./filter";
import HashtagList from "./hashtag-list";
import { MentionListHandle } from "./mention-list";

const hashtagSuggestion = (suggestionRef: RefObject<SuggestionProps[]>) => ({
  items: ({ query }: { query: string }) => {
    const items = suggestionRef.current ?? [];
    if (isQueryAbandoned(query)) return [];
    // Filters and ranks individual children — the previous group-level `.some()`
    // returned every child of a group as soon as one of them matched.
    return filterSuggestionGroups(items, query);
  },

  render: () => {
    let component: ReactRenderer<MentionListHandle> | null = null;
    let popup: Instance<Props>[] | null = null;

    return {
      onStart: (props: any) => {
        if (!props.clientRect) {
          return;
        }

        component = new ReactRenderer(HashtagList, {
          props: { ...props, query: props.query },
          editor: props.editor,
        });

        popup = tippy("body", {
          getReferenceClientRect: props.clientRect as GetReferenceClientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "bottom-start",
        });
      },

      onUpdate(props: any) {
        if (!props.clientRect) {
          return;
        }

        component?.updateProps({ ...props, query: props.query });

        popup?.[0]?.setProps({
          getReferenceClientRect: props.clientRect as GetReferenceClientRect,
        });

        if (isQueryAbandoned(props.query)) {
          popup?.[0]?.hide();
        } else {
          popup?.[0]?.show();
        }
      },

      onKeyDown(props: any) {
        // Matches the `@` popup: Escape closes outright.
        if (props.event.key === "Escape") {
          popup?.[0]?.hide();
          return true;
        }

        return component?.ref?.onKeyDown(props) ?? false;
      },

      onExit() {
        popup?.[0]?.destroy();
        popup = null;
        component?.destroy();
        component = null;
      },
    };
  },
});

export default hashtagSuggestion;
